import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useInstructorMode } from '../hooks/useInstructorMode';
import { formatDate, formatShortDate } from '../utils/dateUtils';
import './PlaylistView.css';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Spotify icon as SVG to avoid loading external image
const SpotifyIcon = () => (
  <svg viewBox="0 0 496 512" className="spotify-icon" fill="#1DB954">
    <path d="M248 8C111.1 8 0 119.1 0 256s111.1 248 248 248 248-111.1 248-248S384.9 8 248 8zm100.7 364.9c-4.2 0-6.8-1.3-10.7-3.6-62.4-37.6-135-39.2-206.7-24.5-3.9 1-9 2.6-11.9 2.6-9.7 0-15.8-7.7-15.8-15.8 0-10.3 6.1-15.2 13.6-16.8 81.9-18.1 165.6-16.5 237 26.2 6.1 3.9 9.7 7.4 9.7 16.5s-7.1 15.4-15.2 15.4zm26.9-65.6c-5.2 0-8.7-2.3-12.3-4.2-62.5-37-155.7-51.9-238.6-29.4-4.8 1.3-7.4 2.6-11.9 2.6-10.7 0-19.4-8.7-19.4-19.4s5.2-17.8 15.5-20.7c27.8-7.8 56.2-13.6 97.8-13.6 64.9 0 127.6 16.1 177 45.5 8.1 4.8 11.3 11 11.3 19.7-.1 10.8-8.5 19.5-19.4 19.5zm31-76.2c-5.2 0-8.4-1.3-12.9-3.9-71.2-42.5-198.5-52.7-280.9-29.7-3.6 1-8.1 2.6-12.9 2.6-13.2 0-23.3-10.3-23.3-23.6 0-13.6 8.4-21.3 17.4-23.9 35.2-10.3 74.6-15.2 117.5-15.2 73 0 149.5 15.2 205.4 47.8 7.8 4.5 12.9 10.7 12.9 22.6 0 13.6-11 23.3-23.2 23.3z"/>
  </svg>
);

function PlaylistView() {
  const { id } = useParams();
  const { buildUrl, isInstructorMode, authLoading } = useInstructorMode();
  
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Ratings state. `rating` is the public {avg, count}; `adminRating`
  // holds the full breakdown + rank + submissions list, fetched only for
  // logged-in instructors.
  const [rating, setRating] = useState(null);
  const [adminRating, setAdminRating] = useState(null);
  const [qrOpen, setQrOpen] = useState(false);

  const fetchPlaylist = useCallback(async () => {
    // Wait for auth check to complete before fetching
    if (authLoading) return;
    
    try {
      setLoading(true);
      
      // Use backend param (?show=all) when in instructor mode
      let url = `${API_BASE}/playlists/${id}`;
      if (isInstructorMode) {
        url += '?show=all';
      }
      
      const response = await axios.get(url);
      setPlaylist(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load playlist');
      console.error('Error fetching playlist:', err);
    } finally {
      setLoading(false);
    }
  }, [id, isInstructorMode, authLoading]);

  useEffect(() => {
    fetchPlaylist();
  }, [fetchPlaylist]);

  // Public rating pill — fires for everyone.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/ratings/playlist/${id}`)
      .then((r) => { if (!cancelled) setRating(r.data); })
      .catch(() => { /* best-effort */ });
    return () => { cancelled = true; };
  }, [id]);

  // Admin breakdown + rank — only for logged-in instructors.
  useEffect(() => {
    if (!id || !isInstructorMode) return;
    let cancelled = false;
    axios
      .get(`${API_BASE}/ratings/admin/playlist/${id}`, { withCredentials: true })
      .then((r) => { if (!cancelled) setAdminRating(r.data); })
      .catch(() => { /* if forbidden, silently hide */ });
    return () => { cancelled = true; };
  }, [id, isInstructorMode]);

  const getArtistLinks = (artists) => {
    return artists.map((artist, index) => (
      <span key={artist.id}>
        <a 
          href={buildUrl(`/artist/${artist.id}`)}
          className="artist-link"
        >
          {artist.name}
        </a>
        {index < artists.length - 1 && ', '}
      </span>
    ));
  };

  // NEW: Helper to determine if a track has special roles
  const getSpecialRole = (position) => {
    if (!playlist.opener_position && !playlist.closer_position) return [];
    
    const roles = [];
    
    if (position === playlist.opener_position) roles.push('opener');
    if (position === playlist.slow_song_position) roles.push('slow');
    if (position === playlist.sprint_position) roles.push('sprint');
    if (position === playlist.closer_position) roles.push('closer');
    
    return roles;
  };

  if (loading) {
    return (
      <div className="playlist-view loading">
        <div className="spinner"></div>
        <p>Loading playlist...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="playlist-view error">
        <h2>Error</h2>
        <p>{error}</p>
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="playlist-view error">
        <h2>Playlist not found</h2>
      </div>
    );
  }

  return (
    <div className="playlist-view">
      <div className="playlist-header">
        <div className="playlist-title-section">
          <h1>{formatDate(playlist.playlist_date)}</h1>
          {playlist.is_sold_out && (
            <span className="sold-out-badge-large">🔥 Sold Out</span>
          )}
          <a 
            href={playlist.spotify_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="spotify-link"
            title="Open in Spotify"
          >
            <SpotifyIcon />
          </a>
        </div>
        <p className="playlist-info">
          {playlist.total_songs} songs
          {rating && rating.count > 0 && (
            <span className="info-pill">
              {' '}· ★ {rating.avg.toFixed(1)}{' '}
              <span className="info-pill-n">({rating.count})</span>
            </span>
          )}
        </p>
      </div>

      {isInstructorMode && adminRating && adminRating.aggregate.count > 0 && (
        <div className="rating-block-admin">
          <div className="rating-block-admin-bar">
            <div className="rating-big">
              <span className="star">★</span>
              <span className="num">{adminRating.aggregate.avg.toFixed(1)}</span>
              <span className="of">/ 5</span>
            </div>
            <div className="rating-meta">
              <b>{adminRating.aggregate.count} rating{adminRating.aggregate.count === 1 ? '' : 's'}</b>
            </div>

            {adminRating.rank?.composite && (
              <div className="rank-block">
                <span className="rank-lbl">Rank</span>
                <span className="rank-num">
                  <span className="hash">#</span>
                  {adminRating.rank.composite.position}
                  <span className="of">of {adminRating.rank.composite.denom}</span>
                </span>
                <span className="rank-pct">
                  top {Math.max(1, Math.round((adminRating.rank.composite.position / adminRating.rank.composite.denom) * 100))}%
                </span>
              </div>
            )}

            <div className="rating-dims">
              {['music', 'instructing', 'other'].map((dim) => {
                const v = adminRating.perDim?.[dim];
                const rank = adminRating.rank?.[dim];
                const label = dim === 'instructing' ? 'Instr' : dim[0].toUpperCase() + dim.slice(1);
                if (!v) {
                  return (
                    <div className="dim" key={dim}>
                      <span className="lbl">{label}</span>
                      <span className="val muted">—</span>
                    </div>
                  );
                }
                let rankClass = '';
                if (rank) {
                  if (rank.position === 1) rankClass = 'best';
                  else if (rank.position / rank.denom > 0.7) rankClass = 'bad';
                }
                return (
                  <div className="dim" key={dim}>
                    <span className="lbl">{label}</span>
                    <span className="val"><span className="star">★</span>{v.avg.toFixed(1)}</span>
                    {rank && (
                      <span className={`rank ${rankClass}`}>
                        {rank.position === 1 ? '#1 all-time' : `#${rank.position} of ${rank.denom}`}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rating-block-admin-actions">
            <button type="button" className="action-link" onClick={() => setQrOpen(true)}>
              📱 Show QR
            </button>
            <a className="action-link" href={`/admin/ratings?playlist=${playlist.id}`}>
              View {adminRating.submissions.length} submission{adminRating.submissions.length === 1 ? '' : 's'} →
            </a>
          </div>
        </div>
      )}

      {qrOpen && (
        <div className="qr-modal" onClick={() => setQrOpen(false)}>
          <div className="qr-modal-inner" onClick={(e) => e.stopPropagation()}>
            <button className="qr-close" onClick={() => setQrOpen(false)} aria-label="Close">×</button>
            <div className="qr-window-tag">Rating window · 9:30am – 12:00pm MT</div>
            <h2>{formatShortDate(playlist.playlist_date)}</h2>
            <p className="qr-sub">Scan to rate today's ride</p>
            <div className="qr-card">
              <img
                alt="QR code"
                src={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&margin=0&data=${encodeURIComponent(`${window.location.origin}/rate/${playlist.id}`)}`}
              />
            </div>
            <div className="qr-url">{window.location.origin}/rate/{playlist.id}</div>
          </div>
        </div>
      )}

      <div className="playlist-table-container">
        <table className="playlist-table">
          <thead>
            <tr>
              <th className="col-position">#</th>
              <th className="col-song">SONG</th>
              <th className="col-bpm">BPM</th>
              <th className="col-plays-last">PLAYS / LAST</th>
            </tr>
          </thead>
          <tbody>
            {playlist.tracks.map((track) => (
              <tr key={track.position}>
                <td className="col-position">{track.position}</td>
                <td className="col-song">
                  <div className="song-info">

                    {track.song_spotify_url ? (
                      <a 
                        href={buildUrl(`/song/${track.song_id}`)}
                        rel="noopener noreferrer"
                        className="song-link"
                      >
                        {track.song_name}
                      </a>
                    ) : (
                      <span className="local-file-name" title="Local file">
                        <a 
                          href={buildUrl(`/song/${track.song_id}`)}
                          rel="noopener noreferrer"
                          className="song-link"
                        >
                          📁 {track.song_name}
                        </a>
                      </span>
                    )}

                    
                    <div className="artist-info">

                    {getSpecialRole(track.position).length > 0 && (
                      <span className="special-role-pills">
                        {getSpecialRole(track.position).map(role => (
                          <span key={role} className={`special-pill ${role}`}>
                            {role === 'slow' ? 'Slow Song' : role}
                          </span>
                        ))}
                      </span>
                    )}

                      {getArtistLinks(track.artists)}
                    </div>
                  </div>
                </td>
                  <td className="col-bpm instructor-bpm" data-label="BPM">
                    {track.bpm || ' '}
                  </td>
                <td className="col-plays-last">
                  {track.total_plays > 1 ? (
                    <div className="plays-last-combined">
                      <div className="play-info">
                        <span className="icon">▶ </span>
                        <a href={buildUrl(`/song/${track.song_id}`)} className="plays-link">
                          {track.total_plays}
                        </a>
                      </div>
                      {track.last_played_playlist_id && (
                        <div className="date-info">
                          <a href={buildUrl(`/playlist/${track.last_played_playlist_id}`)} className="playlist-link">
                            {formatShortDate(track.last_played_date)}
                          </a>
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="plays-last-combined">
                        <span className="debut-text">👀</span>
                      </div>
                      <div className="date-info">Debut</div>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="playlist-actions">
        <a href={buildUrl('/')} className="btn-back">← Back to All Playlists</a>
      </div>
    </div>
  );
}

export default PlaylistView;